import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, User, RefreshCw, Building, CheckCircle } from 'lucide-react';
import { OwnerReassignment } from './OwnerReassignment';
import { formatDisplayName } from '@/lib/formatName';
import type { PolicyDetail, PolicyVersion } from '@/hooks/usePolicy';

interface ReviewOwnershipTabProps {
  policy: PolicyDetail | undefined;
  isLoading: boolean;
  latestVersion: PolicyVersion | null;
}

interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}

function DetailRow({ icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-b-0">
      <div className="text-muted-foreground">{icon}</div>
      <div className="flex-1">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-medium">{value ?? '—'}</p>
      </div>
    </div>
  );
}

export function ReviewOwnershipTab({ policy, isLoading, latestVersion }: ReviewOwnershipTabProps) {
  if (isLoading) {
    return (
      <div className="grid gap-6 md:grid-cols-2">
        <Skeleton className="h-[200px]" />
        <Skeleton className="h-[200px]" />
      </div>
    );
  }

  if (!policy) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ownership</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <DetailRow
              icon={<User className="h-4 w-4" />}
              label="Assigned To"
              value={policy.owner_profile ? formatDisplayName(policy.owner_profile.full_name, policy.owner_profile.credentials) : undefined}
            />
            <DetailRow
              icon={<Building className="h-4 w-4" />}
              label="Clinic"
              value={policy.clinic?.name}
            />
            <DetailRow
              icon={<CheckCircle className="h-4 w-4" />}
              label="Approved On"
              value={latestVersion?.approved_at ? format(new Date(latestVersion.approved_at), 'MMMM d, yyyy') : undefined}
            />
            <DetailRow
              icon={<User className="h-4 w-4" />}
              label="Approved By"
              value={latestVersion?.approved_by_profile ? formatDisplayName(latestVersion.approved_by_profile.full_name, latestVersion.approved_by_profile.credentials) : undefined}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review Schedule</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <DetailRow
              icon={<Calendar className="h-4 w-4" />}
              label="Effective Date"
              value={format(new Date(policy.effective_date), 'MMMM d, yyyy')}
            />
            <DetailRow
              icon={<RefreshCw className="h-4 w-4" />}
              label="Review Cadence"
              value={`Every ${policy.review_cadence_months} month${policy.review_cadence_months !== 1 ? 's' : ''}`}
            />
            <DetailRow
              icon={<Calendar className="h-4 w-4" />}
              label="Last Review"
              value={policy.last_review_date ? format(new Date(policy.last_review_date), 'MMMM d, yyyy') : 'Not yet reviewed'}
            />
            <DetailRow
              icon={<Calendar className="h-4 w-4" />}
              label="Next Review Due"
              value={format(new Date(policy.next_review_date), 'MMMM d, yyyy')}
            />
          </CardContent>
        </Card>
      </div>

      {/* Owner reassignment section (admin only) */}
      <OwnerReassignment policy={policy} />
    </div>
  );
}
